import { useEffect, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Reveal, SectionHeading } from "./Section";

const INTERESTS = ["Partnership", "Investment", "Fundraising", "Business Acquisition", "Selling a Business", "Technology", "Government Innovation", "International Expansion", "Agriculture", "Healthcare", "Aviation", "Other"];

const EMPTY = { name: "", company: "", country: "", email: "", phone: "", message: "" };

const ImpactForm = ({ preset }) => {
  const [form, setForm] = useState(EMPTY);
  const [interests, setInterests] = useState([]);
  const [type, setType] = useState("general");
  const [related, setRelated] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (preset?.type) setType(preset.type);
    if (preset?.related) setRelated(preset.related);
  }, [preset]);

  const toggleInterest = (i) => setInterests((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setSending(true);
    try {
      await api.post("/queries", { ...form, interests, type, related });
      toast.success("Thank you — your message has been received. I'll be in touch soon.");
      setForm(EMPTY); setInterests([]); setType("general"); setRelated("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#E2C08D]/60 focus:outline-none focus:ring-1 focus:ring-[#E2C08D]/40 transition-colors";

  return (
    <section id="impact" className="relative py-28 md:py-36 bg-[#0C0F16]" data-testid="impact-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <SectionHeading index="11" eyebrow="Create Impact" title="What can we build together?" />
            <Reveal delay={0.1}>
              <div className="space-y-5 text-base md:text-lg text-slate-300 leading-relaxed">
                <p>The most interesting opportunities are often found between industries, countries and people who have never spoken to each other.</p>
                <ul className="space-y-2.5 text-slate-400">
                  {["What problem are you trying to solve?", "What opportunity do you see?", "What business are you building?", "What market are you trying to enter?", "What impact do you want to create?"].map((q) => (
                    <li key={q} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-4 shrink-0 rounded-full bg-[#E2C08D]/60" />{q}</li>
                  ))}
                </ul>
                <p className="font-display italic text-xl text-[#E2C08D]/90">Tell me — this is not another business pitch. It's a conversation.</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#10141d] p-7 md:p-9" data-testid="impact-form">
              {type !== "general" && (
                <div className="mb-6 rounded-xl border border-[#E2C08D]/30 bg-[#E2C08D]/8 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-[#E2C08D]" data-testid="impact-preset-banner">
                  {type.replace("-", " ")}{related ? ` — ${related}` : ""}
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <input className={inputCls} placeholder="Name *" value={form.name} onChange={set("name")} data-testid="impact-name-input" />
                <input className={inputCls} placeholder="Company / Organisation" value={form.company} onChange={set("company")} data-testid="impact-company-input" />
                <input className={inputCls} placeholder="Country" value={form.country} onChange={set("country")} data-testid="impact-country-input" />
                <input className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="impact-email-input" />
                <input className={`${inputCls} sm:col-span-2`} placeholder="WhatsApp / Phone" value={form.phone} onChange={set("phone")} data-testid="impact-phone-input" />
              </div>
              <p className="mt-6 mb-3 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">I'm interested in</p>
              <div className="flex flex-wrap gap-2" data-testid="impact-interests">
                {INTERESTS.map((i) => (
                  <button type="button" key={i} onClick={() => toggleInterest(i)} data-testid={`impact-interest-${i.toLowerCase().replace(/\s+/g, "-")}`} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${interests.includes(i) ? "border-[#E2C08D] bg-[#E2C08D]/15 text-[#E2C08D]" : "border-white/10 text-slate-400 hover:text-slate-200"}`}>
                    {i}
                  </button>
                ))}
              </div>
              <textarea className={`${inputCls} mt-5 min-h-[120px] resize-y`} placeholder="Tell me more…" value={form.message} onChange={set("message")} data-testid="impact-message-input" />
              <button type="submit" disabled={sending} data-testid="impact-submit-button" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#E2C08D] px-7 py-3.5 text-sm font-semibold text-[#0A0C10] hover:bg-[#f0d3a4] disabled:opacity-60 transition-colors sm:w-auto">
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Submit Query
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ImpactForm;
